import { areAttachmentValueEqual } from '../properties/reducers';
import { BorrowerActionReturns } from './actions';
import * as Types from './types';
import { BuildRentType, LoanType } from '@apply/constants';

export const updatedExplanations = (currently: Types.CurrentlyQuestions): string => {
    if (currently) {
        const {
            defendantInLegalActions,
            involvedInJudgements,
            delinquentOnDebts,
            delinquentOnTaxes,
            financiallyWorseOff,
            explanations,
        } = currently;
        if (defendantInLegalActions || involvedInJudgements || delinquentOnDebts || delinquentOnTaxes || financiallyWorseOff) {
            return explanations;
        }
    }

    return '';
};

export const borrowerReducer = (
    state: Types.BorrowerState = Types.borrowerInitialState,
    action: BorrowerActionReturns
): Types.BorrowerState => {
    //eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { type, payload } = action as any;
    switch (type) {
        case Types.RESET: {
            return {
                ...Types.borrowerInitialState,
            };
        }
        case Types.UPDATE_LOAN_TYPE: {
            const isARentType = payload.loanType === LoanType.Rental || payload.loanType === LoanType.BuildRent;
            const usingPropertyManagementCompany = isARentType ? state.usingPropertyManagementCompany : undefined;
            const propertyManagementCompany = isARentType ? state.propertyManagementCompany : undefined;
            const contactInfoSameAsPropertyContact = isARentType ? state.contactInfoSameAsPropertyContact : undefined;
            const buildRentTypeId =
                payload.loanType === LoanType.BuildRent ? (state.buildRentTypeId ?? BuildRentType.BuildRent) : undefined;
            return {
                ...state,
                loanType: payload.loanType,
                isDirty: true,
                usingPropertyManagementCompany,
                propertyManagementCompany,
                contactInfoSameAsPropertyContact,
                buildRentTypeId: buildRentTypeId,
            };
        }
        case Types.UPDATE_CONFIRM_ENTITY_TYPE: {
            const entity = { ...state.entity };
            entity.typeId = payload.entityTypeId;
            return {
                ...state,
                entity,
            };
        }
        case Types.UPDATE_FIRST_LOAN_APPLICATION_WITH_BFI: {
            const howDidYouHear = payload.response ? state.howDidYouHear : '';
            const currentlyQuestions = {
                ...state.currentlyQuestions,
                financiallyWorseOff: undefined,
            };
            return {
                ...state,
                firstLoanApplicationWithBfi: payload.response,
                howDidYouHear,
                currentlyQuestions,
                isDirty: true,
            };
        }
        case Types.UPDATE_HOW_DID_YOU_HEAR: {
            return {
                ...state,
                howDidYouHear: payload.heard,
                isDirty: true,
            };
        }
        case Types.UPDATE_CURRENTLY_QUESTIONS_EXPLANATIONS: {
            const currently = { ...state.currentlyQuestions };
            currently.explanations = payload.explanations;

            return {
                ...state,
                currentlyQuestions: currently,
                isDirty: true,
            };
        }
        case Types.UPDATE_DEFENDANT_IN_LEGAL_ACTIONS: {
            const currently = { ...state.currentlyQuestions };
            currently.defendantInLegalActions = payload.response;
            currently.explanations = updatedExplanations(currently);

            return {
                ...state,
                currentlyQuestions: currently,
                isDirty: true,
            };
        }
        case Types.UPDATE_DELINQUENT_ON_DEBTS: {
            const currently = { ...state.currentlyQuestions };
            currently.delinquentOnDebts = payload.response;
            currently.explanations = updatedExplanations(currently);

            return {
                ...state,
                currentlyQuestions: currently,
                isDirty: true,
            };
        }
        case Types.UPDATE_DELINQUENT_ON_TAXES: {
            const currently = { ...state.currentlyQuestions };
            currently.delinquentOnTaxes = payload.response;
            currently.explanations = updatedExplanations(currently);

            return {
                ...state,
                currentlyQuestions: currently,
                isDirty: true,
            };
        }
        case Types.UPDATE_FINANCIALLY_WORSE_OFF: {
            const currently = { ...state.currentlyQuestions };
            currently.financiallyWorseOff = payload.response;
            currently.explanations = updatedExplanations(currently);

            return {
                ...state,
                currentlyQuestions: currently,
                isDirty: true,
            };
        }
        case Types.UPDATE_INVOLVED_IN_JUDGEMENTS: {
            const currently = { ...state.currentlyQuestions };
            currently.involvedInJudgements = payload.response;
            currently.explanations = updatedExplanations(currently);

            return {
                ...state,
                currentlyQuestions: currently,
                isDirty: true,
            };
        }
        case Types.UPDATE_USING_PROPERTY_MANAGEMENT: {
            // NOTE - the lack of spread operator on the true case is very deliberate and NOT a bug
            const propertyManagementCompany = payload.response
                ? state.propertyManagementCompany
                : { ...Types.propertyManagementCompanyInitialState };
            return {
                ...state,
                usingPropertyManagementCompany: payload.response,
                propertyManagementCompany,
                isDirty: true,
            };
        }
        case Types.UPDATE_PROPERTY_MANAGEMENT_COMPANY_NAME: {
            const propertyManagementCompany = {
                ...state.propertyManagementCompany,
            };
            propertyManagementCompany.name = payload.name;
            return {
                ...state,
                propertyManagementCompany,
                isDirty: true,
            };
        }
        case Types.UPDATE_PROPERTY_MANAGEMENT_COMPANY_WEBSITE: {
            const propertyManagementCompany = {
                ...state.propertyManagementCompany,
            };
            propertyManagementCompany.website = payload.website;
            return {
                ...state,
                propertyManagementCompany,
                isDirty: true,
            };
        }
        case Types.UPDATE_PROPERTY_MANAGEMENT_COMPANY_CONTACT_NAME: {
            const propertyManagementCompany = {
                ...state.propertyManagementCompany,
            };
            propertyManagementCompany.contactName = {
                ...Types.fullNameInitialState,
                ...state.propertyManagementCompany.contactName,
                ...payload.name,
            };
            return {
                ...state,
                propertyManagementCompany,
                isDirty: true,
            };
        }
        case Types.UPDATE_PROPERTY_MANAGEMENT_COMPANY_CONTACT_EMAIL: {
            const propertyManagementCompany = {
                ...state.propertyManagementCompany,
            };
            propertyManagementCompany.contactEmail = payload.email;
            return {
                ...state,
                propertyManagementCompany,
                isDirty: true,
            };
        }
        case Types.UPDATE_PROPERTY_MANAGEMENT_COMPANY_CONTACT_PHONE: {
            const propertyManagementCompany = {
                ...state.propertyManagementCompany,
            };
            propertyManagementCompany.contactPhone = payload.phone;
            return {
                ...state,
                propertyManagementCompany,
                isDirty: true,
            };
        }
        case Types.UPDATE_PRINCIPAL_NAME: {
            const principal = { ...state.principal };
            principal.name = {
                ...Types.fullNameInitialState,
                ...state.principal.name,
                ...payload.name,
            };
            return {
                ...state,
                principal,
                isDirty: true,
            };
        }
        case Types.UPDATE_CONTACT_INFO_SAME_AS_PROPERTY: {
            // NOTE - the lack of spread operator on the true case is very deliberate and NOT a bug
            const principal = payload.response ? state.principal : { ...Types.principalInitialState };
            return {
                ...state,
                contactInfoSameAsPropertyContact: payload.response,
                principal,
                isDirty: true,
            };
        }
        case Types.UPDATE_PRINCIPAL_EMAIL: {
            const principal = { ...state.principal };
            principal.email = payload.email;
            return {
                ...state,
                principal,
                isDirty: true,
            };
        }
        case Types.UPDATE_PRINCIPAL_SECONDARY_EMAIL: {
            const principal = { ...state.principal };
            principal.secondaryEmail = payload.email;
            return {
                ...state,
                principal,
                isDirty: true,
            };
        }
        case Types.UPDATE_PRINCIPAL_PHONE: {
            const principal = { ...state.principal };
            principal.phone = payload.phone;
            return {
                ...state,
                principal,
                isDirty: true,
            };
        }
        case Types.UPDATE_BORROWER_ID: {
            return {
                ...state,
                id: payload.id,
                isDirty: true,
            };
        }
        case Types.UPDATE_ENTITY_ID: {
            const entity = { ...state.entity };
            entity.id = payload.id;
            return {
                ...state,
                entity,
                isDirty: true,
            };
        }
        case Types.UPDATE_ENTITY_NAME: {
            const entity = { ...state.entity };
            entity.name = payload.entity;
            return {
                ...state,
                entity,
                isDirty: true,
            };
        }
        case Types.UPDATE_ENTITY_STATE_INCORPORATED: {
            const entity = { ...state.entity };
            entity.stateIncorporated = payload.stateIncorporated;
            if (entity?.address && !entity.address?.state) {
                entity.address.state = payload.stateIncorporated;
            }
            return {
                ...state,
                entity,
                isDirty: true,
            };
        }
        case Types.UPDATE_BORROWER_MASTERNOTEID: {
            return {
                ...state,
                masterNoteId: payload.masterNoteId,
                isDirty: true,
            };
        }
        case Types.UPDATE_ENTITY_ADDRESS_STREET1: {
            const entity = { ...state.entity };
            entity.address.street1 = payload.street1;
            return {
                ...state,
                entity,
                isDirty: true,
            };
        }
        case Types.UPDATE_ENTITY_ADDRESS_STREET2: {
            const entity = { ...state.entity };
            entity.address.street2 = payload.street2;
            return {
                ...state,
                entity,
                isDirty: true,
            };
        }
        case Types.UPDATE_ENTITY_ADDRESS_CITY: {
            const entity = { ...state.entity };
            entity.address.city = payload.city;
            return {
                ...state,
                entity,
                isDirty: true,
            };
        }
        case Types.UPDATE_ENTITY_ADDRESS_STATE: {
            const entity = { ...state.entity };
            entity.address.state = payload.state;
            return {
                ...state,
                entity,
                isDirty: true,
            };
        }
        case Types.UPDATE_ENTITY_ADDRESS_ZIPCODE: {
            const entity = { ...state.entity };
            entity.address.zipCode = payload.zipCode;
            return {
                ...state,
                entity,
                isDirty: true,
            };
        }
        case Types.REPLACE_BORROWER_STATE: {
            return { ...state, ...payload.borrower, isDirty: false };
        }
        case Types.UPDATE_BORROWER_INFO_FIRSTLOAN: {
            return { ...state, isFirstLoan: payload.isFirstLoan };
        }
        case Types.UPDATE_BORROWER_INFO_SHOULDASKLEGAL: {
            return {
                ...state,
                shouldAskLegalQuestions: payload.shouldAskLegalQuestions,
            };
        }
        case Types.UPDATE_IS_DIRTY: {
            return { ...state, isDirty: payload.isDirty };
        }
        case Types.UPDATE_ESIGN: {
            return { ...state, esign: { ...payload.esign } };
        }
        case Types.UPDATE_API_ID: {
            if (payload?.token) {
                return {
                    ...state,
                    apiId: payload.apiId,
                    token: payload?.token,
                };
            } else {
                return { ...state, apiId: payload.apiId };
            }
        }
        case Types.GUEST_ACKNOWLEDGED_NO_PERSONAL_LOANS: {
            return {
                ...state,
                guestAcknowledgedNoPersonalLoans: payload.response,
            };
        }
        case Types.UPDATE_MANAGEMENT_AGREEMENT: {
            if (areAttachmentValueEqual(state.propertyManagementCompany?.managementAgreement, payload.value)) {
                return state;
            }
            const propertyManagementCompany: Types.PropertyManagementCompany = {
                ...Types.propertyManagementCompanyInitialState,
                ...state.propertyManagementCompany,
                managementAgreement: { ...payload.value },
            };
            return {
                ...state,
                propertyManagementCompany,
            };
        }
        case Types.UPDATE_FINALIZE_THIRD_PARTY_CLAIM: {
            return { ...state, finalizeThirdPartyClaim: payload.state };
        }
        case Types.UPDATE_FINALIZE_THIRD_PARTY_NAME: {
            return { ...state, finalizeThirdPartyName: payload.state };
        }
        default: {
            return state;
        }
    }
};
